'use client';

import { ENGINE_PATH_PREFIX } from '@gnsx/genesys.js';
import { ChevronDownIcon, XIcon } from 'lucide-react';
import * as React from 'react';

import type { commandVariants } from '@/components/ui/command';
import type { IAssetFile } from '@/editor/store/models/assetTree';
import type { VariantProps } from 'class-variance-authority';

import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AssetThumbnail } from '@/editor/components/asset-browser/AssetThumbnail';
import { assetsStore } from '@/editor/store';
import { cn } from '@/lib/utils';

export interface AssetThumbnailComboboxOption {
  label: string;
  value: string;
  asset?: IAssetFile;
}

export interface AssetThumbnailComboboxGroup {
  label?: string;
  options: AssetThumbnailComboboxOption[];
}

interface AssetThumbnailComboboxProps {
  options?: AssetThumbnailComboboxOption[];
  groups?: AssetThumbnailComboboxGroup[];
  displayedOptions?: AssetThumbnailComboboxOption[];
  displayedGroups?: AssetThumbnailComboboxGroup[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'default';
  showTooltip?: boolean;
  allowNone?: boolean;
  noneLabel?: string;
  commandVariant?: VariantProps<typeof commandVariants>['variant'];
  footer?: React.ReactNode;
  isModal?: boolean;
  thumbnailSize?: number; // Size for thumbnails in pixels
  triggerThumbnailSize?: number; // Size for thumbnail in the trigger button
  /** Shown on the trigger when `value` is set but not present in options (e.g. missing asset path). */
  valueFallbackLabel?: string;
  /** Extra classes for the trigger label (e.g. destructive when path is invalid). */
  triggerValueClassName?: string;
}

export const AssetThumbnailCombobox = React.forwardRef<HTMLButtonElement, AssetThumbnailComboboxProps>(({
  options,
  groups,
  displayedOptions,
  displayedGroups,
  value,
  onValueChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search options...',
  emptyText = 'No options found.',
  disabled = false,
  className,
  size = 'default',
  showTooltip = true,
  allowNone = true,
  noneLabel = '',
  commandVariant,
  footer,
  isModal = false,
  thumbnailSize = 20,
  triggerThumbnailSize = 20,
  valueFallbackLabel,
  triggerValueClassName,
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false);
  const popoverElementRef = React.useRef<HTMLDivElement | null>(null);
  const effectiveGroups = React.useMemo(() => displayedGroups ?? groups, [displayedGroups, groups]);
  const effectiveOptions = React.useMemo(() => displayedOptions ?? options ?? [], [displayedOptions, options]);

  const allOptions = React.useMemo(() => {
    if (effectiveGroups) {
      return effectiveGroups.flatMap(group => group.options);
    }
    return effectiveOptions;
  }, [effectiveGroups, effectiveOptions]);

  const selectedOption = React.useMemo(() =>
    allOptions.find(option => option.value === value),
  [allOptions, value]
  );

  // Get asset file for the selected value to show thumbnail
  const selectedAsset = React.useMemo(() => {
    if (selectedOption?.asset) return selectedOption.asset;
    if (!value || value === 'none' || !value.trim()) return null;
    return assetsStore.assetTree.findFile(value);
  }, [selectedOption, value]);

  const hasNonEmptyValue = Boolean(value && String(value).trim() !== '' && value !== 'none');
  const displayValue =
    selectedOption?.label
    ?? (value === 'none' && allowNone ? noneLabel : undefined)
    ?? (hasNonEmptyValue && valueFallbackLabel ? valueFallbackLabel : undefined)
    ?? placeholder;
  const tooltipValue = selectedOption?.value || (value === 'none' && allowNone ? 'None' : value || 'None');

  const handleSelect = (selectedValue: string) => {
    onValueChange(selectedValue);
    setOpen(false);
  };

  const handleClearValue = () => {
    onValueChange('');
    setOpen(false);
  };

  /** Custom filter function that searches in both label and value */
  const customFilter = React.useCallback((value: string, search: string, keywords?: string[]) => {
    const searchLower = search.toLowerCase();
    const valueLower = value.toLowerCase();
    const keywordsMatch = keywords?.some(k => k.toLowerCase().includes(searchLower)) ?? false;
    const valueMatch = valueLower.includes(searchLower);
    return keywordsMatch || valueMatch ? 1 : 0;
  }, []);

  React.useEffect(() => {
    if (open && value) {
      const scrollToSelected = () => {
        const popoverElement = popoverElementRef.current;
        const selectedItem = popoverElement?.querySelector(`[data-value="${value}"]`);
        selectedItem?.scrollIntoView({
          behavior: 'instant',
          block: 'center',
        });
      };
      requestAnimationFrame(scrollToSelected);
    }
  }, [open, value]);

  /** Render option with thumbnail */
  const renderOption = (option: AssetThumbnailComboboxOption, isSelected: boolean) => {
    const asset = option.asset;

    return (
      <div className="flex items-center gap-2 w-full">
        {asset && (
          <AssetThumbnail
            asset={asset}
            iconSize={thumbnailSize}
            selected={false}
          />
        )}
        {showTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="truncate flex-1">{option.label}</span>
            </TooltipTrigger>
            <TooltipContent>{option.value}</TooltipContent>
          </Tooltip>
        ) : (
          <span className="truncate flex-1">{option.label}</span>
        )}
      </div>
    );
  };

  const buttonContent = (
    <div className="flex items-center gap-2 w-full min-w-0">
      {selectedAsset && (
        <AssetThumbnail
          asset={selectedAsset}
          iconSize={triggerThumbnailSize}
          selected={false}
        />
      )}
      <span
        className={cn(
          'flex-1 text-left truncate px-0.5 text-xs min-w-0',
          value?.startsWith(ENGINE_PATH_PREFIX) && 'text-engine-asset',
          triggerValueClassName,
        )}
      >
        {displayValue}
      </span>
    </div>
  );

  return (
    <Popover modal={isModal} open={open && !disabled} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          type="button"
          variant="select"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between',
            'text-secondary-foreground',
            disabled && 'opacity-60',
            className
          )}
          size={size}
          disabled={disabled}
          {...props}
        >
          {showTooltip ? (
            <Tooltip>
              <TooltipTrigger asChild>
                {buttonContent}
              </TooltipTrigger>
              <TooltipContent align="start">{tooltipValue}</TooltipContent>
            </Tooltip>
          ) : (buttonContent)}
          <ChevronDownIcon className="size-4 mr-0.5 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        ref={popoverElementRef}
        side="bottom"
        align="start"
        sideOffset={4}
        avoidCollisions={true}
      // className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command variant={commandVariant} defaultValue={value || '-'} filter={customFilter}>
          <div className="relative">
            <CommandInput
              name="search"
              placeholder={searchPlaceholder}
              autoFocus={false}
              variant={commandVariant}
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-bwignore
              aria-autocomplete="none"
            />
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {value && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="clear"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={handleClearValue}
                    >
                      <XIcon className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Clear selection
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
          <CommandList variant={commandVariant}
          >
            <CommandEmpty variant={commandVariant}>{emptyText}</CommandEmpty>
            {effectiveGroups ? (
              <>
                {allowNone && noneLabel && (
                  <CommandGroup variant={commandVariant}>
                    <CommandItem
                      className={cn('data-[selected=true]:bg-transparent data-[selected=true]:text-secondary-foreground', value === 'none' ? 'bg-secondary text-secondary-foreground hover:bg-secondary' : '')}
                      key="none"
                      value="none"
                      keywords={[noneLabel]}
                      data-value="none"
                      variant={commandVariant}
                      onSelect={() => handleSelect('none')}
                    >
                      <span className="text-xs">{noneLabel}</span>
                    </CommandItem>
                  </CommandGroup>
                )}
                {effectiveGroups.map((group, groupIndex) => {
                  const groupHeadingClass = cn(
                    '**:[[cmdk-group-heading]]:text-[11px] **:[[cmdk-group-heading]]:text-popover-foreground **:[[cmdk-group-heading]]:font-medium',
                  );

                  return (
                    <React.Fragment key={group.label || `group-${groupIndex}`}>
                      {(allowNone && groupIndex === 0) && <CommandSeparator variant={commandVariant} />}
                      <CommandGroup variant={commandVariant} heading={group.label} className={groupHeadingClass}>
                        {group.options.map((option) => (
                          <CommandItem
                            className={cn('data-[selected=true]:bg-hover pl-4',
                              value === option.value ? 'bg-primary data-[selected=true]:bg-primary' : '',
                              option.value.startsWith(ENGINE_PATH_PREFIX) ? 'text-engine-asset data-[selected=true]:text-engine-asset-light' : 'data-[selected=true]:text-primary-foreground',
                              value === option.value && option.value.startsWith(ENGINE_PATH_PREFIX) ? 'text-engine-asset-light' : '',
                              value === option.value && !option.value.startsWith(ENGINE_PATH_PREFIX) ? 'text-primary-foreground' : '')}
                            key={option.value}
                            value={option.value}
                            keywords={[option.label]}
                            data-value={option.value}
                            variant={commandVariant}
                            onSelect={() => handleSelect(option.value)}
                          >
                            {renderOption(option, value === option.value)}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </React.Fragment>
                  );
                })}
              </>
            ) : (
              <CommandGroup variant={commandVariant}>
                {allowNone && (
                  <CommandItem
                    className={cn('data-[selected=true]:bg-transparent data-[selected=true]:text-secondary-foreground', value === 'none' ? 'bg-secondary text-secondary-foreground hover:bg-secondary' : '')}
                    key="none"
                    value="none"
                    keywords={[noneLabel]}
                    data-value="none"
                    variant={commandVariant}
                    onSelect={() => handleSelect('none')}
                  >
                    <span className="text-xs">{noneLabel}</span>
                  </CommandItem>
                )}
                {effectiveOptions.map((option) => (
                  <CommandItem
                    className={cn('data-[selected=true]:bg-primary',
                      value === option.value ? 'bg-primary' : '',
                      option.value.startsWith(ENGINE_PATH_PREFIX) ? 'text-engine-asset data-[selected=true]:text-engine-asset-light' : 'data-[selected=true]:text-primary-foreground',
                      value === option.value && option.value.startsWith(ENGINE_PATH_PREFIX) ? 'text-engine-asset-light' : '',
                      value === option.value && !option.value.startsWith(ENGINE_PATH_PREFIX) ? 'text-primary-foreground' : '')}
                    key={option.value}
                    value={option.value}
                    keywords={[option.label]}
                    data-value={option.value}
                    variant={commandVariant}
                    onSelect={() => handleSelect(option.value)}
                  >
                    {renderOption(option, value === option.value)}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
          {footer && <div className="py-1 px-2 border-t border-border">{footer}</div>}
        </Command>
      </PopoverContent>
    </Popover>
  );
});

AssetThumbnailCombobox.displayName = 'AssetThumbnailCombobox';
